import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import Cookies from 'js-cookie';
import loadable from '@loadable/component';
import Api from 'services/Api';
import Auth from 'modules/Auth';
import User from 'modules/User';
import reform from 'components/Reform';
import Fieldset from 'components/Fieldset';
import Button from 'components/Button';
import FormError from 'components/FormError';
import { Typography, TypographyVariant } from 'components/Typography';
import { getExperiment } from 'lib/analytics';
import { isLoading } from 'lib/redux-utils';
import { clearReturningPlayerCookie } from 'lib/returningPlayer';
import { loginPassword as passwordRegex } from 'lib/valRegexes';
import { usernameEmail as usernameEmailValidation } from 'lib/valFunctors';

const Turnstile = loadable(() => import('components/Turnstile'));

const disableTurnstileCookie = Cookies.get('turnstile_exclude') === 'true';
const isTurnstileEnabled =
  !disableTurnstileCookie &&
  __ENV__.CF_TURNSTILE_SITE_KEY &&
  !getExperiment('turnstile-login-off')?.key;

const getUsername = ({ search }) =>
  Object.fromEntries(new URLSearchParams(search).entries()).username;

const Form = reform({
  forceErrorsVisible: true
})(
  ({
    username,
    password,
    onSubmit,
    handleFocus,
    loading,
    formValid,
    errorMessage,
    handleChange,
    showEditableButton,
    handleClearReturningPlayer
  }) => (
    <form className="loginform" onSubmit={onSubmit} name="login">
      <div className="loginform__usernameFieldContainer">
        <Fieldset
          className="loginform__usernameField"
          field={username}
          inputType="text"
          name="username"
          autoComplete="username"
          labelText="Email"
          onChange={handleChange}
          onFocus={handleFocus}
          testId="login-username"
          hideAsterisk
          editable={!showEditableButton}
        />
        {showEditableButton && (
          <Button
            name="clearReturningPlayer"
            className="button--fakeLink loginform__changeButton"
            onClick={handleClearReturningPlayer}
          >
            <Typography variant={TypographyVariant.BodySmStrong}>
              <span>Change</span>
            </Typography>
          </Button>
        )}
      </div>
      <Fieldset
        field={password}
        inputType="password"
        autoComplete="current-password"
        name="password"
        labelText="Password"
        onChange={handleChange}
        onFocus={handleFocus}
        testId="login-password"
        hideAsterisk
      />
      <div className="fieldset fieldset--centerflex">
        <Button
          disabled={!formValid}
          loading={loading}
          title="Sign in to MrQ"
          buttonText="Log in"
          className="button--fullWidth"
          type="submit"
          data-test-id="login-button"
        />
      </div>
      <FormError errorMessage={errorMessage} />
    </form>
  )
);

export class _LoginForm extends Component {
  constructor(props) {
    super(props);
    this.turnstileRef = React.createRef();
    this.state = {
      isSubmitting: false
    };
  }

  getFields = () => ({
    username: {
      initial: this.props.returningPlayerEmail || getUsername(this.props.location),
      required: true,
      error: (value, msg) => msg || `${value} is not a valid username`,
      hint: "You can't change this later",
      onChange: this.props.onChangeUsername
    },
    password: {
      required: true,
      onChange: passwordRegex,
      // eslint-disable-next-line max-len -- long copy
      hint: "Password has insane requirements. It cannot be your dog's name, nor your father's last name. It must contain at least 1 number, 1 lowercase letter and 1 uppercase prayer to the sun god Ra",
      error: 'Invalid credentials'
    }
  });

  onSubmitError = () => {
    this.setState({ isSubmitting: false });
    if (isTurnstileEnabled) {
      this.turnstileRef.current.reset();
    }
  };

  submitForm = async (data) => {
    this.setState({ isSubmitting: true });
    let challengeToken;
    if (isTurnstileEnabled) {
      try {
        challengeToken = await this.turnstileRef.current.getResponsePromise();
      } catch {
        // Always submit login to BE regardless of Turnstile failing
        await this.props.submitWithChallenge()(data);
        throw new Error('Turnstile Challenge Timeout Error');
      }
    }
    // TODO: Remove this when Turnstile experiment is over
    await this.props.submitWithChallenge(challengeToken)(data);
  };

  render() {
    return (
      <>
        <Form
          {...this.props}
          fields={this.getFields()}
          submit={this.submitForm}
          onError={this.onSubmitError}
          loading={this.props.loading || this.state.isSubmitting}
          showEditableButton={this.props.returningPlayerEmail}
          handleClearReturningPlayer={this.props.clearReturningPlayerFromCookie}
        />
        {isTurnstileEnabled ? <Turnstile ref={this.turnstileRef} action="login" /> : null}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: isLoading(state, [Auth.AT.LOGIN._, Auth.AT.FINGERPRINT._]),
  returningPlayerEmail: User.selectors.getReturningPlayer(state)?.email
});

const mapDispatchToProps = (dispatch) => ({
  submitWithChallenge: (challengeToken) => async (data) => {
    const username = data.username.trim();
    const { password } = data;

    const params = new FormData();
    params.append('username', username);
    params.append('password', password);
    const { value: deviceInfo } = await dispatch(Auth.actions.getDeviceFingerprint());
    if (deviceInfo) {
      params.append('deviceInfo', deviceInfo);
    }
    if (isTurnstileEnabled) {
      params.append('challenge', challengeToken);
    }

    return Api.actions.auth.login(null, params)(dispatch, true);
  },
  onChangeUsername: usernameEmailValidation(dispatch),
  clearReturningPlayerFromCookie: () => {
    clearReturningPlayerCookie();
    dispatch(User.actions.clearReturningPlayer());
  }
});

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(_LoginForm);
